/**
 * Turns a raw run/step exception (SAP OData blobs, book-invoker errors, Python
 * tracebacks) into a concise human headline plus the full raw detail for a
 * "Technical details" fold. Generic on purpose — it degrades gracefully for any
 * error shape rather than special-casing one backend.
 */
export interface IFormattedRunError {
  /** One-line, human-readable message (the signal, without the traceback noise). */
  headline: string;
  /** Backend error code if present (e.g. `MM_IV_SI_API/001`). */
  code?: string;
  /** HTTP-ish status if present (e.g. `400`). */
  status?: string;
  /** The full original error, lightly de-escaped, for the fold. */
  detail: string;
  /** True when the headline is a shortened extract (so a details fold is useful). */
  truncated: boolean;
}

function deEscape(s: string): string {
  return s
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '  ')
    .replace(/\\\\/g, '\\');
}

/** Collapse whitespace and trim a captured message to one clean line. */
function cleanMessage(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

export function formatRunError(
  raw: string | undefined | null,
  t: (key: string) => string,
): IFormattedRunError {
  const original = (raw ?? '').trim();
  // De-escape first so all matching runs on real quotes/newlines — backends
  // embed the OData JSON as an escaped string (`\"code\":\"…\"`).
  const detail = deEscape(original);
  if (!original) {
    return {
      headline: t('automation.manifestSopView.step.genericFailure'),
      detail: '',
      truncated: false,
    };
  }

  const status = detail.match(/Status code:\s*(\d{3})/i)?.[1];
  const code = detail.match(/"code"\s*:\s*"([^"]+)"/)?.[1];
  const done = (headline: string): IFormattedRunError => ({
    headline,
    code,
    status,
    detail,
    truncated:
      cleanMessage(detail) !== headline || detail.length > headline.length + 8,
  });

  // 1) SAP/OData: "message":{ … "value":"<human message>" }
  const sapVal = detail.match(
    /"message"\s*:\s*\{[^}]*?"value"\s*:\s*"((?:[^"\\]|\\.)*)"/,
  );
  if (sapVal) return done(cleanMessage(sapVal[1]));

  // 2) A typed error prefix (ValueError: …), up to a status/quote/newline
  // boundary. The quote boundary matters when the typed error itself sits
  // inside a quoted `message: "…"` field with no "(Status code" annotation
  // and no newline before its closing quote (e.g. a book-invoker error) —
  // without it, the non-greedy capture runs past the string's real end and
  // swallows trailing structured fields (`", details: [], metadata: …`)
  // until it hits the next actual newline, which may be arbitrarily far
  // away (e.g. inside an embedded stack trace).
  const typed = detail.match(
    /\b(?:ValueError|TypeError|KeyError|IndexError|RuntimeError|AssertionError|Exception|Error)\s*:\s*(.+?)(?:\s*\(Status code|"|\n|$)/,
  );
  if (typed) return done(cleanMessage(typed[1]));

  // 3) A book-invoker `message: "<msg>"` field, up to a status/quote boundary.
  const bookMsg = detail.match(/\bmessage:\s*"(.+?)(?:\s*\(Status code|\n|")/);
  if (bookMsg) return done(cleanMessage(bookMsg[1]));

  // 4) Fallback: first meaningful (non-Traceback) line, capped.
  const firstLine =
    detail
      .split('\n')
      .map(s => s.trim())
      .find(s => s && !/^Traceback/i.test(s)) ?? detail;
  const headline =
    firstLine.length > 200 ? `${firstLine.slice(0, 197)}…` : firstLine;
  return { headline, code, status, detail, truncated: headline !== detail };
}
